import type { ReactNode } from "react";
import { Badge } from "@freightsimple/ui-core/Display/Badge";
import {
  Dropdown,
  type DropdownOption,
} from "@freightsimple/ui-core/Controls/Dropdown";
import {
  FormItem,
  type ErrorMessageType,
} from "@freightsimple/ui-core/FormLayout/FormItem";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { ShowFormErrors } from "@freightsimple/ui-core/FormLayout/ShowFormErrors";
import { T } from "@freightsimple/ui-core/Typography/T";
import { getTemperatureHandlingTypesForDropdown } from "@/Data/TemperatureHandlingTypes";
// Value import (not `import type`): TemperatureHandling is an enum used at
// runtime (TemperatureHandling.ProtectFromFreezing below), so `import type`
// would erase it and throw "TemperatureHandling is not defined" at render.
import { TemperatureHandling } from "@freightsimple/generated-dashboard-openapi-client/models/TemperatureHandling";

import { LineItemsTemperatureHandlingQuestionBubble } from "../Bubbles/LineItemsTemperatureHandlingQuestionBubble";

interface TemperatureHandlingInputProps {
  temperatureHandling: TemperatureHandling;
  onChange: (value: TemperatureHandling) => void;
  errorMessage?: ErrorMessageType | undefined;
  forceValidation?: boolean | undefined;
  /**
   * When true, the pickup date falls outside the season window in which we
   * offer freeze protection, so Protect From Freezing is rendered disabled
   * with an "Outside of season" badge. Defaults to false for call sites with
   * no pickup date in scope (e.g. saved commodities).
   */
  protectFromFreezingOutOfSeason?: boolean | undefined;
}

// Trailing-underscore badge rendered after the Protect From Freezing label when
// the pickup date is out of season. Inline so the reason sits beside the option
// the customer is trying to pick rather than only as an error below the field.
// Same Badge as the "Not currently supported" pill next to it (see
// Data/TemperatureHandlingTypes), so the two read as one family.
function OutOfSeasonBadge_() {
  return (
    <Badge
      label="Outside of season"
      variant="neutral"
      size="small"
      shape="pill"
    />
  );
}

const LineItemsTemperatureHandlingInput: React.FC<
  TemperatureHandlingInputProps
> = (props: TemperatureHandlingInputProps) => {
  const outOfSeason = props.protectFromFreezingOutOfSeason ?? false;

  const rawOptions = getTemperatureHandlingTypesForDropdown();

  // ui-core's DropdownOption has no `trailingIcon` field, so the "Not currently
  // supported" pill can't ride on the option object. Keep a value → pill lookup
  // and render it in renderOption by value instead.
  const trailingIconByValue = new Map<string, ReactNode>(
    rawOptions.map((option) => [option.value, option.trailingIcon]),
  );

  const options: DropdownOption[] = rawOptions.map((option) => {
    const base: DropdownOption = {
      value: option.value,
      label: option.label,
      disabled: option.disabled,
    };
    if (
      option.value === TemperatureHandling.ProtectFromFreezing &&
      outOfSeason
    ) {
      return { ...base, disabled: true };
    }
    return base;
  });

  return (
    <ShowFormErrors show={Boolean(props.forceValidation)}>
      <FormItem
        label={
          <>
            Temperature Requirements
            <LineItemsTemperatureHandlingQuestionBubble />
          </>
        }
        error={props.errorMessage}
      >
        <Dropdown
          options={options}
          value={props.temperatureHandling}
          onChange={(v) => props.onChange(v as TemperatureHandling)}
          width="full"
          renderOption={(option) => (
            <Row gap={2}>
              <T truncate>{option.label}</T>
              {/* Forward the option's own trailing icon — ENG-5376's
                  "Not currently supported" pill on Keep Refrigerated / Keep
                  Frozen lives here. A custom renderOption replaces the whole
                  row, so dropping this silently hides those pills. */}
              {trailingIconByValue.get(option.value)}
              {option.value === TemperatureHandling.ProtectFromFreezing &&
                outOfSeason && <OutOfSeasonBadge_ />}
            </Row>
          )}
          data-testid="temperatureHandlingControl"
        />
      </FormItem>
    </ShowFormErrors>
  );
};
export default LineItemsTemperatureHandlingInput;
